import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { ClipLoader } from 'react-spinners';
import styled from 'styled-components';

// API
import { getInvitations } from '../../api/invitations';

// Components
import EventCard from '../organisms/EventCard';
import Footer from '../organisms/Footer';
import Modal from '../organisms/Modal';

const Root = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: flex-start;
    min-height: 100vh;
`;

const Container = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: flex-start;
    width: 100%;
`;

const Row = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    align-items: flex-start;
    max-width: 1200px;
    width: 100%;
    margin: 0 auto;
    column-gap: 8px;
    row-gap: 24px;

    @media (max-width: 480px) {
        flex-direction: column;
        margin-bottom: 8px;
        max-width: 90%;
        margin-left: auto;
        margin-right: auto;
    }

    @media (min-width: 768px) and (max-width: 1200px) {
        max-width: 95%;
    }

    @media (min-width: 480px) and (max-width: 768px) {
        max-width: 90%;
    }
`;

const FullColumn = styled.div`
    flex: 1;
    width: 100%;

    @media (max-width: 480px) {
        width: 100%;
        flex: 1;
    }
`;

const HalfColumn = styled.div`
    width: 50%;

    @media (max-width: 480px) {
        width: 100%;
    }
`;

const RsvpCard = styled.div`
    border-radius: 12px;
    background-color: #eaeaea;
    width: auto; height: auto;
    padding: 18px;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: flex-start;
`;

const PageTitle = styled.h1`
    font-size: 42px;
    font-weight: 700;
    text-align: center;
    margin: 0;
    padding: 0;

    @media (max-width: 480px) {
        font-size: 36px;
    }
`;

const PageDescription = styled.p`
    font-size: 18px;
    font-weight: 400;
    text-align: center;
    margin: 0;
    padding: 0;
    margin-top: 12px;

    @media (max-width: 480px) {
        font-size: 16px;
    }
`;

const PageButton = styled.button`
    font-size: 18px;
    font-weight: 700;
    color: #fff;
    background-color: #56050D;
    border: none;
    border-radius: 8px;
    padding: 12px 24px;
    margin-top: 24px;
    margin-bottom: 0px;
    margin-left: auto;
    margin-right: auto;
    cursor: pointer;
    transition: all 0.2s ease-in-out;

    &:hover {
        background-color: #6e0b12;
    }
`;

const InvitationsPage = (props) => {
    const { personId } = useParams();
    const [invitations, setInvitations] = useState([]);
    const [currentPerson, setCurrentPerson] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    const [contactModalOpen, setContactModalOpen] = useState(false);

    useEffect(() => {
        if (personId) { fetchInvitations(); }
    }, [personId]);

    useEffect(() => {
        if (invitations && invitations.length > 0) {
            if (currentPerson === null) {
                const person = getCurrentPersonFromInvitations();
                if (person !== null) { setCurrentPerson(person); }
            }

            if (isLoading) { setIsLoading(false); }
        }
    }, [invitations]);

    const fetchInvitations = async (setLoading = true) => {
        if (setLoading) { setIsLoading(true); }
        setError(null);

        let invitations = [];
        try {
            invitations = await getInvitations(personId);
            setInvitations(invitations);
            if (setLoading) { setIsLoading(false); }
        } catch (error) {
            console.error(`Error fetching invitations for person ${personId}: ${error}`);
            setError(error);
            if (setLoading) { setIsLoading(false); }
            return;
        }
    }

    const onChangeRsvp = () => {
        // TODO: Create a backend endpoint to get only one invitation
        fetchInvitations(false);
    }

    const getCurrentPersonFromInvitations = () => {
        if (invitations && invitations.length > 0) {
            for (let i = 0; i < invitations.length; i++) {
                const invitation = invitations[i];
                if (invitation.family && invitation.family.people && invitation.family.people.length > 0) {
                    for (let j = 0; j < invitation.family.people.length; j++) {
                        const person = invitation.family.people[j];
                        if (person.id === personId) {
                            return person;
                        }
                    }
                }
            }
        }
        return null;
    }

    return (
        <Root>
            <Container style={{ padding: '32px 0' }}>
                <Row>
                    <FullColumn style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        <img src={`${process.env.PUBLIC_URL}/header-logo.png`} alt="Invitations" style={{ height: '128px', width: 'auto', margin: 'auto' }} />
                    </FullColumn>
                </Row>
            </Container>
            <Container>
                <Row>
                    <FullColumn style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        <ClipLoader color="#56050D" loading={isLoading} size={96} cssOverride={{ borderWidth: '8px', marginTop: '92px', marginBottom: '92px' }} />
                    </FullColumn>
                </Row>
            </Container>
            {!isLoading && currentPerson && invitations && invitations.length > 0 && (
                <>
                    <Container>
                        <Row>
                            <FullColumn style={{ paddingTop: 0, paddingBottom: 32 }}>
                                <PageTitle>Welcome, {currentPerson.name.split(' ')[0]}!</PageTitle>
                                <PageDescription>You are invited to the following events.</PageDescription>
                                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                    <PageButton onClick={() => setContactModalOpen(true)}>Contact Us</PageButton>
                                </div>
                            </FullColumn>
                        </Row>
                    </Container>
                    <Container style={{ paddingBottom: 32 }}>
                        <Row>
                            <FullColumn>
                                {invitations.map((invitation, index) => {
                                    return (
                                        <EventCard
                                            key={index}
                                            personId={personId}
                                            invitation={invitation}
                                            onChangeRsvp={onChangeRsvp}
                                        />
                                    );
                                })}
                            </FullColumn>
                        </Row>
                    </Container>
                </>
            )}
            <Footer />

            <Modal isOpen={contactModalOpen} onClose={() => setContactModalOpen(false)}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                    <h3 style={{ marginTop: 0, marginBottom: 0, fontSize: 22 }}>Contact</h3>
                    <button onClick={() => setContactModalOpen(false)} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer' }}>✖</button>
                </div>
                <hr />
                <ul>
                    <li style={{ marginBottom: 16 }}>Bikash Dhali: <a href="tel:8473408257">847-340-8257</a></li>
                    <li>Rina Bala: <a href="tel:8473479240">847-347-9240</a></li>
                </ul>
            </Modal>
        </Root>
    );
};

export default InvitationsPage;